import React, { useState, useEffect } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import EmailImageUpload from "../EmailImageUpload";
import { validateImage } from "../../../utils/imageHelper";
import StyleControls from "./StyleControls";

const RightImageLeftContent = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    title = "",
    description = "",
    image = null,
    link = "",
    buttonText = "",
    buttonLink = "",
    component_name = "RightImageLeftContent",
  } = sectionData;

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [localDescription, setLocalDescription] = useState(description);
  const [localImage, setLocalImage] = useState(validateImage(image));
  const [imageUrl, setImageUrl] = useState(link);
  const [localButtonText, setLocalButtonText] = useState(buttonText);
  const [localButtonLink, setLocalButtonLink] = useState(buttonLink);

  // Sync from sectionData when id changes (section loaded/changed)
  useEffect(() => {
    setLocalTitle(title);
    setLocalDescription(description);
    setLocalImage(validateImage(image));
    setLocalButtonText(buttonText);
    setLocalButtonLink(buttonLink);
  }, [id]); // Only sync when section id changes, not on every prop change

  // Handle image URL creation separately - this runs when localImage changes
  useEffect(() => {
    if (localImage instanceof File || localImage instanceof Blob) {
      const url = URL.createObjectURL(localImage);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url); // Clean up
    }

    if (typeof localImage === "string") {
      setImageUrl(localImage);
    }

    if (!localImage) {
      setImageUrl(null);
    }
  }, [localImage]);

  // Merge section-specific styles with parent styles
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  const {
    primaryColor = "#333333",
    secondaryColor = "#a1894c",
    fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
    backgroundColor = "#ffffff"
  } = effectiveStyleSettings;

  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    updateSection(id, {
      id,
      component_name,
      title: localTitle,
      description: localDescription,
      image: localImage,
      buttonText: localButtonText,
      buttonLink: localButtonLink,
      styleSettings: {
        ...(currentSection.styleSettings || {}) // Preserve existing styleSettings
      }
    });
    setIsEditing(false);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component right-image-left-content-editor p-3 mb-3 border rounded">
        <h4>Edit Right Image Left Content</h4>
        <input
          className="form-control mb-2"
          value={localTitle}
          onChange={(e) => setLocalTitle(e.target.value)}
          placeholder="Title"
        />
        <textarea
          className="form-control mb-2"
          value={localDescription}
          onChange={(e) => setLocalDescription(e.target.value)}
          placeholder="Description"
        />
        <EmailImageUpload
          initialImage={localImage}
          onImageUpload={setLocalImage}
        />
        <input
          className="form-control mb-2"
          value={localButtonText}
          onChange={(e) => setLocalButtonText(e.target.value)}
          placeholder="Button Text"
        />
        <input
          className="form-control mb-2"
          value={localButtonLink}
          onChange={(e) => setLocalButtonLink(e.target.value)}
          placeholder="Button Link"
        />
        <div className="d-flex justify-content-end mt-3">
          <button
            className="btn btn-outline-secondary me-2"
            onClick={() => setIsEditing(false)}
          >
            Cancel
          </button>
          <button className="btn bgprimary rounded" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    );
  }

  // VIEW/PREVIEW
  return (
    <>
      {!previewMode && (
        <div className="email-component right-image-left-content mb-3">
          <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <StyleControls 
              id={id} 
              componentName={component_name}
              styleSettings={styleSettings}
              previewMode={previewMode}
            />
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </button>
          </div>
        </div>
      )}
      <table
        width="100%"
        cellpadding="0"
        cellspacing="0"
        border="0"
        style={{ fontFamily, backgroundColor }}
      >
        <tr>
          {/* Left column: Text content */}
          <td
            valign="middle"
            style={{ padding: "24px", fontFamily, backgroundColor }}
          >
            <table cellpadding="0" cellspacing="0" border="0" width="100%">
              <tr>
                <td
                  style={{
                    paddingBottom: "12px",
                    fontFamily,
                  }}
                >
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: secondaryColor,
                      fontFamily,
                    }}
                  >
                    {localTitle}
                  </div>
                </td>
              </tr>
              <tr>
                <td
                  style={{
                    paddingBottom: "12px",
                    fontFamily,
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      color: primaryColor,
                      fontFamily,
                    }}
                  >
                    {localDescription}
                  </div>
                </td>
              </tr>
              {localButtonText && (
                <tr>
                  <td>
                    <a
                      href={localButtonLink}
                      style={{
                        display: "inline-block",
                        padding: "10px 16px",
                        backgroundColor: primaryColor,
                        color: secondaryColor,
                        fontSize: "16px",
                        fontWeight: "bold",
                        textDecoration: "none",
                        fontFamily,
                        borderRadius: "4px",
                      }}
                    >
                      {localButtonText}
                    </a>
                  </td>
                </tr>
              )}
            </table>
          </td>

          {/* Right column: Image */}
          <td width="300" valign="middle" style={{ padding: "0", width: "300px", backgroundColor }}>
            <img
              src={imageUrl || "/banner-placeholder.png"}
              alt="Section"
              style={{
                width: "300px",
                height: "200px",
                display: "block",
                objectFit: "contain",
              }}
            />
          </td>
        </tr>
      </table>
    </>
  );
};

export default RightImageLeftContent;
